import EighteenIcon from "@/components/icons/18-icon";
import TwentyOneIcon from "@/components/icons/21-icon";
import ChildIcon from "@/components/icons/child-icon";
import { AdminTrip } from "@workspace/admin-domain/src/features/trip/schema";
import { t } from "i18next";
import {
  LucideAccessibility,
  LucideBadgeCheck,
  LucideBan,
  LucideCar,
  LucideCheck,
  LucideCircleParking,
  LucideClock,
  LucideEggFried,
  LucideGlobe,
  LucideGoal,
  LucideHandPlatter,
  LucidePawPrint,
  LucidePlane,
  LucideRotateCcwClock,
  LucideWifi,
} from "lucide-react";
import * as z from "zod";

// CONSTANTS

export const TRIP_STATUS_OPTIONS: {
  label: string;
  value: AdminTrip["Trip"]["status"];
}[] = [
  {
    label: t("trips.status.UPCOMING"),
    value: "UPCOMING",
  },
  {
    label: t("trips.status.ONGOING"),
    value: "ONGOING",
  },
  {
    label: t("trips.status.COMPLETED"),
    value: "COMPLETED",
  },
] as const;

export const TRIP_SORT_OPTIONS: {
  label: string;
  kv: `${AdminTrip["SortBy"]}_${AdminTrip["SortOrder"]}`;
  value: {
    key: AdminTrip["SortBy"];
    order: AdminTrip["SortOrder"];
  };
}[] = [
  {
    label: t("trips.sort.startDate_desc"),
    kv: "startDate_desc",
    value: {
      key: "startDate",
      order: "desc",
    },
  },
  {
    label: t("trips.sort.startDate_asc"),
    kv: "startDate_asc",
    value: {
      key: "startDate",
      order: "asc",
    },
  },
  {
    label: t("trips.sort.currentRevenue_asc"),
    kv: "currentRevenue_asc",
    value: {
      key: "currentRevenue",
      order: "asc",
    },
  },
  {
    label: t("trips.sort.currentRevenue_desc"),
    kv: "currentRevenue_desc",
    value: {
      key: "currentRevenue",
      order: "desc",
    },
  },
  {
    label: t("trips.sort.currentReservations_asc"),
    kv: "currentReservations_asc",
    value: {
      key: "currentReservations",
      order: "asc",
    },
  },
  {
    label: t("trips.sort.currentReservations_desc"),
    kv: "currentReservations_desc",
    value: {
      key: "currentReservations",
      order: "desc",
    },
  },
  {
    label: t("trips.sort.price_asc"),
    kv: "adultPrice_asc",
    value: {
      key: "adultPrice",
      order: "asc",
    },
  },
  {
    label: t("trips.sort.price_desc"),
    kv: "adultPrice_desc",
    value: {
      key: "adultPrice",
      order: "desc",
    },
  },
] as const;

export const TRIP_TAG_OPTIONS = {
  audience: {
    groupName: t("trips.tags.audience"),
    groupType: "single",
    options: [
      {
        icon: ChildIcon,
        label: t("trips.tags.familyFriendly"),
        value: "FAMILY",
      },
      {
        icon: EighteenIcon,
        label: t("trips.tags.adults.18"),
        value: "ADULTS_18",
      },
      {
        icon: TwentyOneIcon,
        label: t("trips.tags.adults.21"),
        value: "ADULTS_21",
      },
    ],
  },
  food: {
    groupName: t("trips.tags.food"),
    groupType: "single",
    options: [
      {
        icon: LucideBan,
        label: t("none"),
        value: "NONE",
      },
      {
        icon: LucideEggFried,
        label: t("trips.tags.breakfastIncluded"),
        value: "BREAKFAST",
      },
      {
        icon: LucideHandPlatter,
        label: t("trips.tags.halfBoard"),
        value: "HALF",
      },
      {
        icon: LucideBadgeCheck,
        label: t("trips.tags.allInclusive"),
        value: "ALL",
      },
    ],
  },
  accessibility: {
    groupName: t("trips.tags.accessibility"),
    groupType: "multiple",
    options: [
      {
        icon: LucideAccessibility,
        label: t("trips.tags.wheelchairAccessible"),
        value: "WHEELCHAIR",
      },
      {
        icon: LucidePawPrint,
        label: t("trips.tags.petFriendly"),
        value: "PET",
      },
      {
        icon: LucideCircleParking,
        label: t("trips.tags.freeParking"),
        value: "PARKING",
      },
      {
        icon: LucideWifi,
        label: t("trips.tags.freeWiFi"),
        value: "WIFI",
      },
    ],
  },
  transportation: {
    groupName: t("trips.tags.transportation"),
    groupType: "multiple",
    options: [
      {
        icon: LucideCar,
        label: t("trips.tags.transportationProvided"),
        value: "TRANSPORTATION",
      },
      {
        icon: LucidePlane,
        label: t("trips.tags.airportPickup"),
        value: "AIRPORT",
      },
    ],
  },
  guides: {
    groupName: t("trips.tags.guides"),
    groupType: "multiple",
    options: [
      {
        icon: LucideGoal,
        label: t("trips.tags.guidedTour"),
        value: "TOUR",
      },
      {
        icon: LucideGlobe,
        label: t("trips.tags.englishSpeakingGuide"),
        value: "ENGLISH",
      },
    ],
  },
} as const satisfies Partial<{
  [T in keyof AdminTrip["Trip"]]: {
    groupName: string;
    groupType: "single" | "multiple";
    options: {
      icon: React.ElementType;
      label: string;
      value: AdminTrip["Trip"][T] extends Array<infer U> ? U : AdminTrip["Trip"][T];
    }[];
  };
}>;

export const DEFAULT_TRIPS_PARAMS = {
  paginate: undefined,
  limit: 20,
  sortBy: "startDate",
  sortOrder: "desc",
} satisfies AdminTrip["GetAll"];

// FUNCTIONS

export function getTripStatusColor(status: string) {
  switch (status) {
    case "UPCOMING":
      return "blue" as const;
    case "ONGOING":
      return "green" as const;
    case "COMPLETED":
      return "violet" as const;
    default:
      return "gray" as const;
  }
}

export function getTripStatusIcon(status: string) {
  switch (status) {
    case "UPCOMING":
      return <LucideClock className="size-3" />;
    case "ONGOING":
      return <LucideRotateCcwClock className="size-3" />;
    case "COMPLETED":
      return <LucideCheck className="size-3" />;
    default:
      return <LucideClock className="size-3" />;
  }
}

// SCHEMAS

export const TripFiltersSchema = AdminTrip.GetAll.omit({
  paginate: true,
  limit: true,
}).partial({
  sortBy: true,
  sortOrder: true,
});
export type TripFiltersSchema = z.infer<typeof TripFiltersSchema>;
